import { Injectable, inject } from '@angular/core';
import { CanMatchFn } from '@angular/router';
import { REQUEST } from '../../../express.tokens';
import { APP_LOCATION } from './app-location';
import {
  APP_ORIGINS,
  AppSurface,
  absoluteUrl,
  surfaceForHost,
} from './app-surface';

/**
 * The current surface, inside DI.
 *
 * Reads the host from the incoming request under SSR and from the browser's
 * location otherwise, so the route table matches the same way on both sides of a
 * hydration boundary. `REQUEST` is optional because it is only provided by the
 * server bootstrap (see main.server.ts and express.tokens.ts).
 */
@Injectable({ providedIn: 'root' })
export class AppSurfaceService {
  private readonly location = inject(APP_LOCATION);
  private readonly request = inject(REQUEST, { optional: true });

  readonly host: string = this.resolveHost();
  readonly current: AppSurface = surfaceForHost(this.host, APP_ORIGINS);

  urlFor(surface: AppSurface, path: string): string {
    return absoluteUrl(surface, path, APP_ORIGINS);
  }

  private resolveHost(): string {
    const fromRequest = this.request?.headers?.host;
    if (fromRequest) {
      return fromRequest;
    }
    return this.location?.host ?? '';
  }
}

/** Matches only on the estimator surface. */
export const estimatorSurfaceGuard: CanMatchFn = () =>
  inject(AppSurfaceService).current === 'estimator';

/** Matches only on the briefings surface. */
export const briefingsSurfaceGuard: CanMatchFn = () =>
  inject(AppSurfaceService).current === 'briefings';
